/**
 * @since 2026-04-20
 * @author Francesco Parrella
 * @maintainer Francesco Parrella
 * @copyright AlgoTraders, All rights reserved
 */

import { useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

import StatusText from "components/Shared/StatusText";
import ValidateIssueDialog from "components/Shared/ValidateIssueDialog";
import "./IssueList.scss";

const IssueList = ({ issues, canValidate, onValidated, onUnvalidated }) => {
  const [dialogIssue, setDialogIssue] = useState(null);

  if (!issues || issues.length === 0) return null;

  const handleConfirm = async (reason, andRun = false) => {
    const issue = dialogIssue;
    setDialogIssue(null);
    if (issue) await onValidated?.(issue, reason, andRun);
  };

  return (
    <Box className="issueList">
      <Typography variant="subtitle2" className="issueList__header">Flagged items</Typography>
      {issues.map((issue) => {
        const validated = Boolean(issue.validation);
        return (
          <Box key={issue.issueId} className={`issueList__row ${validated ? "issueList__row--validated" : ""}`}>
            <Box className="issueList__rowMain">
              <Box className="issueList__status">
                {validated ? (
                  <StatusText severity="validated" />
                ) : (
                  <StatusText severity={issue.severity || "warn"} />
                )}
              </Box>
              <Typography variant="body2" className="issueList__label">{issue.label}</Typography>
              {validated ? (
                canValidate && (
                  <Tooltip arrow title="Remove the validation you previously set on this item. The issue will return to its flagged state.">
                    <Button
                      size="small"
                      className="issueList__button issueList__button--link"
                      onClick={() => onUnvalidated?.(issue.issueId)}
                    >
                      Un-validate
                    </Button>
                  </Tooltip>
                )
              ) : (
                canValidate && (
                  <Tooltip arrow title="Confirm this specific item is expected. Dismisses the warning for this issue only, with an audit trail (who/when/why).">
                    <Button
                      size="small"
                      className="issueList__button issueList__button--primary"
                      onClick={() => setDialogIssue(issue)}
                    >
                      Validate
                    </Button>
                  </Tooltip>
                )
              )}
            </Box>
            {validated && (
              <Box className="issueList__rowMeta">
                {issue.validation.reason && (
                  <Typography variant="body2" className="issueList__reason">
                    “{issue.validation.reason}”
                  </Typography>
                )}
                <Typography variant="caption" className="issueList__attribution">
                  Validated by {issue.validation.validatedBy || "unknown"}
                  {issue.validation.validatedAt && ` on ${issue.validation.validatedAt}`}
                </Typography>
              </Box>
            )}
          </Box>
        );
      })}

      <ValidateIssueDialog
        open={Boolean(dialogIssue)}
        issue={dialogIssue}
        onConfirm={handleConfirm}
        onCancel={() => setDialogIssue(null)}
      />
    </Box>
  );
};

export default IssueList;
